// Theme Configuration File
// Easy way to manage and switch between different color themes

export const themes = {
  // THEME 1: Ocean Blue (Professional & Trustworthy)
  oceanBlue: {
    name: "Ocean Blue",
    description: "Professional and trustworthy blue theme",
    colors: {
      primary: "#1E88E5",           // Deep ocean blue
      primaryHover: "#1565C0",      // Darker blue on hover
      primaryLight: "#42A5F5",      // Light blue accent
      secondary: "#0277BD",         // Complementary blue
      accent: "#00ACC1",            // Cyan accent
      ribbon: "#80DEEA",            // Light cyan for ribbons
      gradient: {
        start: "#1E88E5",
        end: "#0D47A1"
      },
      // Hero section specific
      hero: {
        gradientFrom: "#0D47A1",    // Dark blue
        gradientTo: "#1565C0",      // Medium blue
        buttonBg: "#00ACC1",        // Cyan button
        buttonHover: "#00BCD4",     // Lighter cyan on hover
      },
      // Subscription banner specific
      subscriptionBanner: {
        gradientFrom: "#004a7c",    // Dark blue
        gradientTo: "#0277BD",      // Medium blue
        inputBg: "#003d66",         // Darker blue for inputs
        buttonBg: "#ffffff",        // White button
        buttonText: "#004a7c",      // Dark blue text
      },
      // Footer specific
      footer: {
        background: "#0D47A1",      // Dark blue background
        text: "#e4e8ec",            // Light gray text
        linkHover: "#42A5F5",       // Light blue on hover
      },
      // Supporting colors
      text: {
        dark: "#060d17",
        light: "#4e4e4e",
      },
      background: {
        header: "#f6f8fb",
        main: "#ffffff",
      },
      border: "#e4e8ec",
    }
  },

  // THEME 2: Forest Green (Natural & Growth)
  forestGreen: {
    name: "Forest Green",
    description: "Natural and growth-oriented green theme",
    colors: {
      primary: "#2E7D32",           // Forest green
      primaryHover: "#1B5E20",      // Dark forest green
      primaryLight: "#43A047",      // Light green
      secondary: "#388E3C",         // Complementary green
      accent: "#66BB6A",            // Bright green accent
      ribbon: "#A5D6A7",            // Light green for ribbons
      gradient: {
        start: "#2E7D32",
        end: "#1B5E20"
      },
      // Hero section specific
      hero: {
        gradientFrom: "#004034",    // Dark teal (original)
        gradientTo: "#00806a",      // Medium teal (original)
        buttonBg: "#3AE943",        // Bright green button (original)
        buttonHover: "#40FE4A",     // Brighter green on hover (original)
      },
      // Subscription banner specific
      subscriptionBanner: {
        gradientFrom: "#004a38",    // Dark teal (original)
        gradientTo: "#006b51",      // Medium teal (original)
        inputBg: "#0d6550",         // Darker teal for inputs (original)
        buttonBg: "#ffffff",        // White button
        buttonText: "#004a38",      // Dark teal text
      },
      // Footer specific
      footer: {
        background: "#1B5E20",      // Dark green background
        text: "#e4e8ec",            // Light gray text
        linkHover: "#66BB6A",       // Bright green on hover
      },
      text: {
        dark: "#060d17",
        light: "#4e4e4e",
      },
      background: {
        header: "#f6f8fb",
        main: "#ffffff",
      },
      border: "#e4e8ec",
    }
  },

  // THEME 3: Royal Purple (Current/Premium & Modern)
  royalPurple: {
    name: "Royal Purple",
    description: "Premium and modern purple theme (current)",
    colors: {
      primary: "#463AE9",           // Royal purple (current)
      primaryHover: "#4F42FF",      // Lighter purple on hover
      primaryLight: "#6B5FFF",      // Light purple accent
      secondary: "#1758a6",         // Blue accent
      accent: "#7C4DFF",            // Bright purple
      ribbon: "#9FF0A3",            // Current green ribbon
      gradient: {
        start: "#463ae9",
        end: "#2315e5"
      },
      // Hero section specific
      hero: {
        gradientFrom: "#2E1F85",    // Dark purple
        gradientTo: "#463AE9",      // Royal purple
        buttonBg: "#9FF0A3",        // Green button (original)
        buttonHover: "#B0FFBB",     // Lighter green on hover
      },
      // Subscription banner specific
      subscriptionBanner: {
        gradientFrom: "#2E1F85",    // Dark purple
        gradientTo: "#5B4CFF",      // Lighter purple
        inputBg: "#1F155C",         // Darker purple for inputs
        buttonBg: "#9FF0A3",        // Green button
        buttonText: "#1F155C",      // Dark purple text
      },
      // Footer specific
      footer: {
        background: "#2315e5",      // Dark purple background
        text: "#e4e8ec",            // Light gray text
        linkHover: "#7C4DFF",       // Bright purple on hover
      },
      text: {
        dark: "#060d17",
        light: "#4e4e4e",
      },
      background: {
        header: "#f6f8fb",
        main: "#ffffff",
      },
      border: "#e4e8ec",
    }
  }
};

// Default active theme
export const DEFAULT_THEME = "oceanBlue"; // Current theme

// Helper function to get theme by name
export const getTheme = (themeName) => {
  return themes[themeName] || themes[DEFAULT_THEME];
};

// Helper function to get all theme names
export const getThemeNames = () => {
  return Object.keys(themes);
};

// CSS variable mapping
export const getCSSVariables = (themeName) => {
  const theme = getTheme(themeName);
  return {
    '--theme-primary': theme.colors.primary,
    '--theme-primary-hover': theme.colors.primaryHover,
    '--theme-primary-light': theme.colors.primaryLight,
    '--theme-secondary': theme.colors.secondary,
    '--theme-accent': theme.colors.accent,
    '--theme-ribbon': theme.colors.ribbon,
    '--theme-gradient-start': theme.colors.gradient.start,
    '--theme-gradient-end': theme.colors.gradient.end,
    '--theme-hero-gradient-from': theme.colors.hero.gradientFrom,
    '--theme-hero-gradient-to': theme.colors.hero.gradientTo,
    '--theme-hero-button-bg': theme.colors.hero.buttonBg,
    '--theme-hero-button-hover': theme.colors.hero.buttonHover,
    '--theme-subscription-gradient-from': theme.colors.subscriptionBanner.gradientFrom,
    '--theme-subscription-gradient-to': theme.colors.subscriptionBanner.gradientTo,
    '--theme-subscription-input-bg': theme.colors.subscriptionBanner.inputBg,
    '--theme-subscription-button-bg': theme.colors.subscriptionBanner.buttonBg,
    '--theme-subscription-button-text': theme.colors.subscriptionBanner.buttonText,
    '--theme-footer-bg': theme.colors.footer.background,
    '--theme-footer-text': theme.colors.footer.text,
    '--theme-footer-link-hover': theme.colors.footer.linkHover,
    '--theme-text-dark': theme.colors.text.dark,
    '--theme-text-light': theme.colors.text.light,
    '--theme-bg-header': theme.colors.background.header,
    '--theme-bg-main': theme.colors.background.main,
    '--theme-border': theme.colors.border,
  };
};
